import { useMemo } from 'react';
import { useSAPDataStore, selectFilteredMachines, selectFilteredLines, selectFilteredFactories } from '../store/SAPDataStore';

/**
 * Hook that provides SAP data with the same interface as the old useBackendDataManager
 * This ensures backward compatibility while using the new centralized store
 */
export const useBackendDataManager = () => {
  const {
    loading,
    error,
    lastFetched,
    fetchSAPData,
    showOnlyWithData,
    getFilteredMachines,
    getFilteredLines,
    getFilteredFactories
  } = useSAPDataStore();

  // Create backward-compatible data state objects
  const machines = useMemo(() => ({
    data: getFilteredMachines(),
    loading,
    error,
    lastFetched
  }), [getFilteredMachines, loading, error, lastFetched]);

  const lines = useMemo(() => ({
    data: getFilteredLines(),
    loading,
    error,
    lastFetched
  }), [getFilteredLines, loading, error, lastFetched]);

  const factories = useMemo(() => ({
    data: getFilteredFactories(),
    loading,
    error,
    lastFetched
  }), [getFilteredFactories, loading, error, lastFetched]);

  // Backward-compatible methods
  const fetchMachines = async (force = false) => {
    // Get token from auth hook or pass null for now
    // The SAPDataProvider will handle token management
    await fetchSAPData(null, force);
  };

  const fetchLines = async (force = false) => {
    await fetchSAPData(null, force);
  };

  const fetchFactories = async (force = false) => {
    await fetchSAPData(null, force);
  };

  const refreshAll = async (force = true) => {
    await fetchSAPData(null, force);
  };

  return {
    machines,
    lines,
    factories,
    loading,
    error,
    lastFetched,
    fetchMachines,
    fetchLines,
    fetchFactories,
    refreshAll,
    showOnlyWithData
  };
};

/**
 * New hook for direct access to the SAP data store
 * Use this for new components that want full access to the store features
 */
export const useSAPData = () => {
  return useSAPDataStore();
};

/**
 * Hook for accessing specific SAP data selectors
 */
export const useSAPDataSelectors = () => {
  const machines = useSAPDataStore(selectFilteredMachines);
  const lines = useSAPDataStore(selectFilteredLines);
  const factories = useSAPDataStore(selectFilteredFactories);
  
  return {
    machines,
    lines,
    factories
  };
};
